import { useState, useEffect } from 'react';
import useLocalStorage from 'use-local-storage';

const useThemeColors = () => {
    const [theme, setTheme] = useLocalStorage("theme", "light");
    const [isDark, setIsDark] = useState(theme === "dark");
    const [colors, setColors] = useState({});

    // Function to get CSS custom property value
    const getCSSProperty = (property) => {
        return getComputedStyle(document.documentElement).getPropertyValue(property).trim();
    };

    // Function to read all CSS custom properties and create colors object
    const updateColorsFromCSS = () => {
        const newColors = {
            // Background colors
            background: getCSSProperty('--background'),
            specialBackground: getCSSProperty('--special-background'),
            
            // Text colors
            textColor: getCSSProperty('--text-color'),
            specialTextColor: getCSSProperty('--special-text-color'),
            
            // Brand colors
            primary: getCSSProperty('--primary'),
            
            // UI colors
            whiteBlack: getCSSProperty('--white-black'),
            whiteBlackBgOpacity: getCSSProperty('--white-black-background-opacity'),
            
            // Shadows
            shadowLg: getCSSProperty('--shadow-lg'),
            shadowMd: getCSSProperty('--shadow-md'),
            shadowSm: getCSSProperty('--shadow-sm'),
            socialCardsShadow: getCSSProperty('--social-cards-shadow'),
            
            // Enhanced gray colors for better dark mode support
            border: isDark ? '#374151' : '#dee2e6',
            secondary: isDark ? '#9ca3af' : '#6c757d',
            textSecondary: isDark ? '#d1d5db' : '#6c757d',
            accent: isDark ? '#51cf66' : '#28a745',
            warning: isDark ? '#ffd43b' : '#ffc107',
            danger: isDark ? '#ff6b6b' : '#dc3545',
            info: isDark ? '#22b8cf' : '#17a2b8',
            
            // New dark mode optimized gray alternatives
            gray50: isDark ? '#1f2937' : '#f9fafb',
            gray100: isDark ? '#374151' : '#f3f4f6',
            gray200: isDark ? '#4b5563' : '#e5e7eb',
            gray300: isDark ? '#6b7280' : '#d1d5db',
            gray400: isDark ? '#9ca3af' : '#9ca3af',
            gray500: isDark ? '#d1d5db' : '#6b7280',
            gray600: isDark ? '#e5e7eb' : '#4b5563',
            gray700: isDark ? '#f3f4f6' : '#374151',
            gray800: isDark ? '#f9fafb' : '#1f2937',
            gray900: isDark ? '#ffffff' : '#111827',
            
            // Text colors for dark mode
            textGray400: isDark ? '#9ca3af' : '#9ca3af',
            textGray500: isDark ? '#d1d5db' : '#6b7280',
            textGray600: isDark ? '#e5e7eb' : '#4b5563',
            textGray700: isDark ? '#f3f4f6' : '#374151',
            textGray800: isDark ? '#f9fafb' : '#1f2937',
            textGray900: isDark ? '#ffffff' : '#111827',
        };
        
        setColors(newColors);
    };

    // Update isDark state and colors when theme changes
    useEffect(() => {
        setIsDark(theme === "dark");
        
        // Small delay to ensure CSS has been applied after theme change
        const timer = setTimeout(updateColorsFromCSS, 10);
        return () => clearTimeout(timer);
    }, [theme, isDark]);

    // Initial color setup
    useEffect(() => {
        updateColorsFromCSS();
    }, []);

    // Toggle theme function
    const toggleTheme = () => {
        const newTheme = theme === "light" ? "dark" : "light";
        setTheme(newTheme);
    };

    return {
        theme,
        isDark,
        isLight: theme === "light",
        colors,
        toggleTheme,
        setTheme,
        updateColors: updateColorsFromCSS, // Allow manual color refresh if needed
        
        // Helper methods for easy access to common colors from CSS
        get backgroundColor() { return getCSSProperty('--background'); },
        get textColor() { return getCSSProperty('--text-color'); },
        get primaryColor() { return getCSSProperty('--primary'); },
        get surfaceColor() { return getCSSProperty('--special-background'); },
        get specialTextColor() { return getCSSProperty('--special-text-color'); },
        
        // Utility function to get any CSS custom property
        getCSSVar: getCSSProperty,
        
        // Helper functions for gray colors that adapt to theme
        getGray50: () => isDark ? '#1f2937' : '#f9fafb',
        getGray100: () => isDark ? '#374151' : '#f3f4f6',
        getGray200: () => isDark ? '#4b5563' : '#e5e7eb',
        getGray300: () => isDark ? '#6b7280' : '#d1d5db',
        getGray400: () => isDark ? '#9ca3af' : '#9ca3af',
        getGray500: () => isDark ? '#d1d5db' : '#6b7280',
        getGray600: () => isDark ? '#e5e7eb' : '#4b5563',
        getGray700: () => isDark ? '#f3f4f6' : '#374151',
        getGray800: () => isDark ? '#f9fafb' : '#1f2937',
        getGray900: () => isDark ? '#ffffff' : '#111827',
        
        // Helper functions for text gray colors
        getTextGray400: () => isDark ? '#9ca3af' : '#9ca3af',
        getTextGray500: () => isDark ? '#d1d5db' : '#6b7280',
        getTextGray600: () => isDark ? '#e5e7eb' : '#4b5563',
        getTextGray700: () => isDark ? '#f3f4f6' : '#374151',
        getTextGray800: () => isDark ? '#f9fafb' : '#1f2937',
        getTextGray900: () => isDark ? '#ffffff' : '#111827',
        
        // Utility function to get theme-aware gray class
        getGrayClass: (shade, type = 'bg') => {
            const grayMap = {
                50: isDark ? 'bg-gray-800' : 'bg-gray-50',
                100: isDark ? 'bg-gray-700' : 'bg-gray-100',
                200: isDark ? 'bg-gray-600' : 'bg-gray-200',
                300: isDark ? 'bg-gray-500' : 'bg-gray-300',
                400: isDark ? 'bg-gray-400' : 'bg-gray-400',
                500: isDark ? 'bg-gray-300' : 'bg-gray-500',
                600: isDark ? 'bg-gray-200' : 'bg-gray-600',
                700: isDark ? 'bg-gray-100' : 'bg-gray-700',
                800: isDark ? 'bg-gray-50' : 'bg-gray-800',
                900: isDark ? 'bg-white' : 'bg-gray-900',
            };
            
            const textGrayMap = {
                400: isDark ? 'text-gray-300' : 'text-gray-400',
                500: isDark ? 'text-gray-200' : 'text-gray-500',
                600: isDark ? 'text-gray-100' : 'text-gray-600',
                700: isDark ? 'text-gray-50' : 'text-gray-700',
                800: isDark ? 'text-white' : 'text-gray-800',
                900: isDark ? 'text-white' : 'text-gray-900',
            };
            
            return type === 'text' ? textGrayMap[shade] || `text-gray-${shade}` : grayMap[shade] || `${type}-gray-${shade}`;
        },
    };
};

export default useThemeColors;